import React from 'react';
import { generatePath, Outlet } from 'react-router';

import { PERMISSIONS } from 'api/auth/constants';
import { CLIENTS_KEYS } from 'api/clients/constants';
import { IClient } from 'api/clients/types';
import { ICONS_MAP } from 'constants/icons';
import { queryClient } from 'queries';
import { normalizeQueryKey } from 'queries/utils';
import { ROUTES } from 'routes/config/constants';
import { IRoutesConfigItem } from 'routes/config/index';
import { appLazy } from 'routes/ErrorBoundary/appReloadLazy';
import ClientsInstrumentsList from 'routes/pages/AuthorizedStack/Clients/ClientsInstrumentsList';

const Clients = appLazy(() => import('routes/pages/AuthorizedStack/Clients/ClientsList'));
const Client = appLazy(
  () => import('routes/pages/AuthorizedStack/Clients/ClientsList/Client/View'),
);

const CreateClient = appLazy(
  () => import('routes/pages/AuthorizedStack/Clients/ClientsList/Client/Create'),
);
const EditClient = appLazy(
  () => import('routes/pages/AuthorizedStack/Clients/ClientsList/Client/Edit'),
);
const ManualPayout = appLazy(() => import('routes/pages/AuthorizedStack/ManualPayout'));
const ManualTransaction = appLazy(
  () => import('routes/pages/AuthorizedStack/ManualTransaction'),
);
const AuditLogs = appLazy(() => import('routes/pages/AuthorizedStack/AuditLogs'));

export const CLIENTS_CHILDREN: IRoutesConfigItem[] = [
  {
    breadcrumb: 'clients.title',
    children: [
      {
        component: Clients,
        path: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_CLIENTS],
      },
      {
        breadcrumb: (params) => {
          if (!params?.id) return '';

          const client = queryClient.getQueryData<IClient>(
            normalizeQueryKey([CLIENTS_KEYS.CLIENT_INFO, params.id]),
          );

          return client
            ? `${client.firstName} ${client.lastName}`
            : params?.id || 'client.title';
        },
        children: [
          {
            component: Client,
            path: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.CLIENT.PATH,
            permissions: [PERMISSIONS.CAN_VIEW_CLIENTS],
          },
          {
            component: EditClient,
            isPrivate: true,
            path: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.EDIT.PATH,
            permissions: [PERMISSIONS.CAN_UPDATE_CLIENT],
          },
          {
            breadcrumb: 'manualPayout.title',
            component: ManualPayout,
            path: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.CLIENT.SUB_PATH
              .MANUAL_PAYOUT.PATH,
            permissions: [PERMISSIONS.CAN_CREATE_MANUAL_PAYOUT],
            url: (params) => {
              const id = params?.id;
              const { PARAMS, PATH } =
                ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.CLIENT.SUB_PATH
                  .MANUAL_PAYOUT;
              return !id
                ? ''
                : generatePath(PATH, {
                    [PARAMS.ID]: id,
                  });
            },
          },
          {
            breadcrumb: 'manualTransaction.title',
            component: ManualTransaction,
            path: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.CLIENT.SUB_PATH
              .MANUAL_TRANSACTION.PATH,
            permissions: [PERMISSIONS.CAN_CREATE_MANUAL_TRANSACTION],
            url: (params) => {
              const id = params?.id;
              const { PARAMS, PATH } =
                ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.CLIENT.SUB_PATH
                  .MANUAL_TRANSACTION;
              return !id
                ? ''
                : generatePath(PATH, {
                    [PARAMS.ID]: id,
                  });
            },
          },
          {
            breadcrumb: 'auditLogs.title',
            component: AuditLogs,
            path: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.CLIENT.SUB_PATH
              .AUDIT_LOGS.PATH,
            permissions: [PERMISSIONS.CAN_VIEW_CLIENTS, PERMISSIONS.VIEW_AUDIT_LOGS],
            url: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.CLIENT.SUB_PATH
              .AUDIT_LOGS.PATH,
          },
          {
            breadcrumb: 'auditLogs.title',
            component: AuditLogs,
            path: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.CLIENT.SUB_PATH
              .AUDIT_LOGS.SUB_PATH.CHILD,
            permissions: [PERMISSIONS.CAN_VIEW_CLIENTS, PERMISSIONS.VIEW_AUDIT_LOGS],
            url: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.CLIENT.SUB_PATH
              .AUDIT_LOGS.SUB_PATH.CHILD,
          },
        ],
        component: Outlet,
        path: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.CLIENT.PATH,
        url: (params) => {
          const id = params?.id;
          const { PARAMS, PATH } =
            ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.CLIENT;
          return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
        },
      },
      {
        breadcrumb: 'client.form.titleCreate',
        component: CreateClient,
        path: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.CREATE,
        permissions: [PERMISSIONS.CAN_CREATE_CLIENT],
        url: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.CREATE,
      },
      {
        breadcrumb: 'manualPayout.title',
        component: ManualPayout,
        path: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.MANUAL_PAYOUT.PATH,
        permissions: [PERMISSIONS.CAN_CREATE_MANUAL_PAYOUT],
        url: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.MANUAL_PAYOUT.PATH,
      },
      {
        breadcrumb: 'manualTransaction.title',
        component: ManualTransaction,
        path: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.MANUAL_TRANSACTION.PATH,
        permissions: [PERMISSIONS.CAN_CREATE_MANUAL_TRANSACTION],
        url: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.SUB_PATH.MANUAL_TRANSACTION.PATH,
      },
    ],
    component: Outlet,
    isPrivate: true,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.ClientsList />,
    path: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_CLIENTS],
    testId: 'navbarClients',
    url: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS.PATH,
  },
  {
    breadcrumb: 'clientsInstruments.title',
    children: [
      {
        component: ClientsInstrumentsList,
        path: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS_INSTRUMENTS.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_PAYMENT_INSTRUMENTS],
      },
    ],
    component: Outlet,
    isPrivate: true,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.ClientsInstruments />,
    path: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS_INSTRUMENTS.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_PAYMENT_INSTRUMENTS],
    testId: 'navbarClientsInstruments',
    url: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS_INSTRUMENTS.PATH,
  },
];
